import { useEffect, useRef } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { subscribeToUserTrips, type Trip } from '@/services/trips';
import { fetchWeatherForecast, getDestinationCoordinates, type WeatherForecast } from '@/services/weather';
import { sendWeatherAlertNotification, sendWeatherForecastNotification } from '@/services/notifications';
import { useUserStore } from '@/state/userStore';
import { sendGatedNotification, DEFAULT_COOLDOWNS } from '@/services/notificationGate';
import { buildDedupeKey } from '@/services/notificationDedupe';

type WeatherAlertState = {
  tripId: string;
  lastChecked: Date;
  lastForecast?: WeatherForecast;
  notifiedAlerts: Set<string>; // Track which alerts we've already notified about
};

type PersistedWeatherAlertState = {
  lastCheckedAt?: number;
  notifiedAlerts?: string[];
  lastForecastKey?: string;
  lastForecastSentAt?: number;
  lastForecastSummary?: string;
};

const WEATHER_ALERTS_STORAGE_KEY = '@tripflow_weather_alert_state_v1';

const safeParseJson = <T,>(raw: string | null): T | null => {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
};

/**
 * Hook to monitor trips and send weather alerts.
 * Checks weather 24-48 hours before trip start and sends alerts for severe weather.
 *
 * If `providedTrips` is supplied, the hook reuses that already-loaded trip list instead of opening
 * a second `subscribeToUserTrips` listener. This keeps the Trips tab from double-subscribing to
 * the same Firestore source during startup.
 */
export const useWeatherAlerts = (providedTrips?: Trip[]) => {
  const firebaseUser = useUserStore((state) => state.firebaseUser);
  const alertStatesRef = useRef<Map<string, WeatherAlertState>>(new Map());
  const unsubscribeRef = useRef<(() => void) | null>(null);
  // Prevent duplicate sends when Firestore subscription fires multiple times in quick succession.
  // We lock per-user processing to avoid overlapping async work, and also lock per-notification-key
  // to prevent duplicate scheduling even if concurrency still happens.
  const processingRef = useRef<boolean>(false);
  const pendingRef = useRef<boolean>(false);
  const latestTripsRef = useRef<Trip[] | null>(null);
  const inFlightNotificationKeysRef = useRef<Set<string>>(new Set());
  const runQueueRef = useRef<(() => Promise<void>) | null>(null);

  const loadPersistedState = async (): Promise<Record<string, PersistedWeatherAlertState>> => {
    const raw = await AsyncStorage.getItem(WEATHER_ALERTS_STORAGE_KEY);
    return safeParseJson<Record<string, PersistedWeatherAlertState>>(raw) || {};
  };

  const savePersistedState = async (next: Record<string, PersistedWeatherAlertState>) => {
    try {
      await AsyncStorage.setItem(WEATHER_ALERTS_STORAGE_KEY, JSON.stringify(next));
    } catch {
      // ignore
    }
  };

  const usesProvidedTrips = providedTrips !== undefined;

  useEffect(() => {
    if (!firebaseUser?.uid) {
      latestTripsRef.current = null;
      runQueueRef.current = null;
      return;
    }

    let cancelled = false;

    const processTrips = async () => {
      const trips = latestTripsRef.current;
      if (!trips || cancelled) return;

      try {
          const now = new Date();
          const twoDaysFromNow = new Date(now.getTime() + 48 * 60 * 60 * 1000);

          // Filter trips that are starting soon (within 48 hours)
          const upcomingTrips = trips.filter(
            (trip) =>
              !trip.isArchived &&
              trip.startDate >= now &&
              trip.startDate <= twoDaysFromNow &&
            trip.destination,
          );

          const persisted = await loadPersistedState();

          for (const trip of upcomingTrips) {
          if (cancelled) return;

            const stateKey = trip.id;
            let state = alertStatesRef.current.get(stateKey);

            if (!state) {
              state = {
                tripId: trip.id,
                lastChecked: new Date(0), // Never checked
                notifiedAlerts: new Set(),
              };
              alertStatesRef.current.set(stateKey, state);
            }

            // Hydrate from persisted state (prevents duplicates across remounts/app restarts)
            const persistedTrip = persisted[trip.id];
            if (persistedTrip?.notifiedAlerts?.length) {
              persistedTrip.notifiedAlerts.forEach((k) => state!.notifiedAlerts.add(k));
            }
            if (persistedTrip?.lastCheckedAt && state.lastChecked.getTime() === 0) {
              state.lastChecked = new Date(persistedTrip.lastCheckedAt);
            }

            // Check if we need to fetch weather (every 6 hours or if never checked)
            const hoursSinceLastCheck = (now.getTime() - state.lastChecked.getTime()) / (1000 * 60 * 60);
            const shouldCheck = hoursSinceLastCheck >= 6 || state.lastChecked.getTime() === 0;

            if (!shouldCheck) {
              continue;
            }

            try {
              // Resolve destination + coordinates (prefer stored coords; avoid ambiguous geocodes like "Chicago")
              let coords:
                | { latitude: number; longitude: number; city: string; country: string }
                | null = null;

              if (trip.destinations && trip.destinations.length > 0) {
                const matchingDest =
                  trip.destinations.find((dest) => dest.startDate <= trip.startDate && dest.endDate >= trip.startDate) ||
                  trip.destinations[0];

                if (matchingDest?.coordinates?.latitude && matchingDest?.coordinates?.longitude) {
                  coords = {
                    latitude: matchingDest.coordinates.latitude,
                    longitude: matchingDest.coordinates.longitude,
                    city: matchingDest.city,
                    country: matchingDest.country || '',
                  };
                } else {
                  const query = matchingDest.country ? `${matchingDest.city}, ${matchingDest.country}` : matchingDest.city;
                  coords = await getDestinationCoordinates(query);
                }
              } else {
                // Legacy: keep the full destination string (often includes country) to improve geocoding.
                const query = (trip.destination || '').trim();
                coords = await getDestinationCoordinates(query);
              }
              
              if (!coords) {
                console.warn(`[Weather Alerts] Could not resolve destination coordinates for trip: ${trip.id}`);
                continue;
              }

              // Fetch weather forecast targeted to the actual trip start date.
              const targetDate = trip.startDate instanceof Date ? new Date(trip.startDate) : new Date(trip.startDate as any);
              targetDate.setHours(0, 0, 0, 0);

              const forecast = await fetchWeatherForecast({
                latitude: coords.latitude,
                longitude: coords.longitude,
                city: coords.city,
                countryCode: coords.country,
                days: 5,
                targetDate: targetDate.toISOString().split('T')[0],
              });

              if (!forecast) {
                console.warn(`[Weather Alerts] Could not fetch forecast for trip: ${trip.id}`);
                continue;
              }

              // Update state
              state.lastChecked = now;
              state.lastForecast = forecast;
              persisted[trip.id] = {
                ...(persisted[trip.id] || {}),
                lastCheckedAt: now.getTime(),
              };

              // Check for severe weather alerts
              if (forecast.alerts && forecast.alerts.length > 0) {
                for (const alert of forecast.alerts) {
                  const alertKey = `${alert.start}-${alert.end}-${alert.title}`;
                const inFlightKey = `weather-alert-${trip.id}-${alertKey}`;
                  
                  // Only notify about high/critical severity alerts
                if (
                  (alert.severity === 'high' || alert.severity === 'critical') &&
                  !state.notifiedAlerts.has(alertKey) &&
                  !inFlightNotificationKeysRef.current.has(inFlightKey)
                ) {
                  inFlightNotificationKeysRef.current.add(inFlightKey);
                  try {
                    // Use gated notification with preference/dedupe/rate-limit checks
                    const sent = await sendGatedNotification(
                      {
                        category: 'safety',
                        channel: 'push',
                        tripId: trip.id,
                        priority: alert.severity === 'critical' ? 'high' : 'medium',
                        dedupeKey: buildDedupeKey.weather(trip.id, 'alert', alertKey),
                        cooldownMs: DEFAULT_COOLDOWNS.weather,
                      },
                      () => sendWeatherAlertNotification(
                        trip.name,
                        trip.id,
                        trip.destination!,
                        alert.title,
                        alert.description,
                        alert.severity,
                      )
                    );
                    if (sent) {
                      state.notifiedAlerts.add(alertKey);
                      const existing = new Set(persisted[trip.id]?.notifiedAlerts || []);
                      existing.add(alertKey);
                      persisted[trip.id] = {
                        ...(persisted[trip.id] || {}),
                        notifiedAlerts: Array.from(existing),
                      };
                    }
                  } finally {
                    inFlightNotificationKeysRef.current.delete(inFlightKey);
                  }
                  }
                }
              }

              // Send pre-trip forecast notification (24-48 hours before)
              const daysUntilTrip = Math.ceil(
              (trip.startDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
              );

              if (daysUntilTrip >= 1 && daysUntilTrip <= 2) {
                // Generate forecast summary
                const startKey = targetDate.toISOString().split('T')[0];
                const startIndex = Math.max(0, forecast.forecast.findIndex((d) => d.date === startKey));
                const startDay = forecast.forecast[startIndex] ?? forecast.forecast[0];
                const avgTemp = Math.round((startDay.temperature.min + startDay.temperature.max) / 2);
                const conditions = forecast.forecast
                  .slice(startIndex, startIndex + 3)
                  .map((day) => day.condition)
                  .join(', ');
                
                const summary = `Expected ${avgTemp}°C with ${conditions.toLowerCase()}. ${
                  startDay.precipitation.probability > 50
                    ? `High chance of rain (${startDay.precipitation.probability}%).`
                    : ''
                }`;

                // Dedupe: once per trip+startDate, and at most every 12h if summary changes.
                const forecastKey = `forecast-${trip.id}-${startKey}`;
              const inFlightKey = `weather-forecast-${forecastKey}`;
              if (
                !state.notifiedAlerts.has(forecastKey) &&
                !inFlightNotificationKeysRef.current.has(inFlightKey)
              ) {
                inFlightNotificationKeysRef.current.add(inFlightKey);
                try {
                  // Use gated notification with preference/dedupe/rate-limit checks
                  const sent = await sendGatedNotification(
                    {
                      category: 'itinerary',
                      channel: 'push',
                      tripId: trip.id,
                      priority: 'medium',
                      dedupeKey: buildDedupeKey.weather(trip.id, 'forecast', startKey),
                      cooldownMs: DEFAULT_COOLDOWNS.weather,
                    },
                    () => sendWeatherForecastNotification(
                      trip.name,
                      trip.id,
                      trip.destination!,
                      summary,
                      daysUntilTrip,
                    )
                  );
                  if (sent) {
                    state.notifiedAlerts.add(forecastKey);
                    const existing = new Set(persisted[trip.id]?.notifiedAlerts || []);
                    existing.add(forecastKey);
                    persisted[trip.id] = {
                      ...(persisted[trip.id] || {}),
                      notifiedAlerts: Array.from(existing),
                      lastForecastKey: forecastKey,
                      lastForecastSentAt: now.getTime(),
                      lastForecastSummary: summary,
                    };
                  }
                } finally {
                  inFlightNotificationKeysRef.current.delete(inFlightKey);
                }
                }
              }

              await savePersistedState(persisted);
            } catch (error) {
              console.error(`[Weather Alerts] Error checking weather for trip ${trip.id}:`, error);
            }
          }

          // Clean up old states (trips that have passed or are more than 7 days old)
          const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          for (const [key, state] of alertStatesRef.current.entries()) {
            const trip = trips.find((t) => t.id === state.tripId);
            if (!trip) {
              // Trip no longer exists - check if it's been more than 7 days since last check
              if (state.lastChecked < sevenDaysAgo) {
                alertStatesRef.current.delete(key);
              }
            } else if (trip.endDate < now) {
              // Trip has ended - clean up after 7 days
              if (state.lastChecked < sevenDaysAgo) {
                alertStatesRef.current.delete(key);
              }
            }
          }
      } catch (error) {
        console.error('[Weather Alerts] Error processing trip subscription update:', error);
      }
    };

    const runQueue = async () => {
      if (cancelled) return;
      if (processingRef.current) {
        pendingRef.current = true;
        return;
      }
      processingRef.current = true;
      try {
        // Process the latest snapshot; if more snapshots arrive while processing, loop once more.
        do {
          pendingRef.current = false;
          await processTrips();
        } while (pendingRef.current && !cancelled);
      } finally {
        processingRef.current = false;
      }
    };

    runQueueRef.current = runQueue;

    if (usesProvidedTrips) {
      return () => {
        cancelled = true;
        if (runQueueRef.current === runQueue) {
          runQueueRef.current = null;
        }
      };
    }

    const unsubscribe = subscribeToUserTrips(firebaseUser.uid, async (trips: Trip[]) => {
      if (cancelled) return;
      latestTripsRef.current = trips;
      await runQueue();
    }, {
      onError: (error) => {
        if (__DEV__) {
          console.warn('[Weather Alerts] subscribeToUserTrips error:', error);
        }
      },
    });

    unsubscribeRef.current = unsubscribe;

    return () => {
      cancelled = true;
      try {
        unsubscribeRef.current?.();
      } catch {
        // ignore
      } finally {
        unsubscribeRef.current = null;
        if (runQueueRef.current === runQueue) {
          runQueueRef.current = null;
        }
      }
    };
  }, [firebaseUser?.uid, usesProvidedTrips]);

  useEffect(() => {
    if (!firebaseUser?.uid || !usesProvidedTrips) {
      return;
    }

    latestTripsRef.current = providedTrips ?? [];
    void runQueueRef.current?.();
  }, [firebaseUser?.uid, usesProvidedTrips, providedTrips]);
};

